import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

export type SidebarGroups = 'admin' | 'csh' | 'alarm' | 'help';

export interface SidebarVisibilityState {
  admin: boolean;
  csh: boolean;
  alarm: boolean;
  help: boolean;
  addedCsh: boolean;
  // per-CSH-group visibility map keyed by groupId
  cshGroupVisibility: Record<number, boolean>;
  setGroupVisibility: (group: SidebarGroups, visible: boolean) => void;
  setCshGroupVisibility: (groupId: number, visible: boolean) => void;
  setAddedCshVisibility: (visible: boolean) => void;
}

const defaultState: SidebarVisibilityState = {
  admin: true,
  csh: true,
  alarm: false,
  help: true,
  addedCsh: true,
  cshGroupVisibility: {},
  setGroupVisibility: () => {},
  setCshGroupVisibility: () => {},
  setAddedCshVisibility: () => {},
};

const SidebarVisibilityContext = createContext<SidebarVisibilityState>(defaultState);

export const SidebarVisibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarVisibility');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Backwards-compatibility: if saved value was simple booleans, normalize shape
        return {
          admin: parsed.admin ?? true,
          csh: parsed.csh ?? true,
          alarm: parsed.alarm ?? false,
          help: parsed.help ?? true,
          addedCsh: parsed.addedCsh ?? true,
          cshGroupVisibility: parsed.cshGroupVisibility ?? {},
        };
      }
      return { admin: true, csh: true, alarm: false, help: true, addedCsh: true, cshGroupVisibility: {} };
    } catch {
      return { admin: true, csh: true, alarm: false, help: true, addedCsh: true, cshGroupVisibility: {} };
    }
  });

  useEffect(() => {
    localStorage.setItem('sidebarVisibility', JSON.stringify(state));
  }, [state]);

  const setGroupVisibility = useCallback((group: SidebarGroups, visible: boolean) => {
    setState(prev => ({ ...prev, [group]: visible }));
  }, []);

  const setCshGroupVisibility = useCallback((groupId: number, visible: boolean) => {
    setState(prev => ({
      ...prev,
      cshGroupVisibility: { ...(prev.cshGroupVisibility || {}), [groupId]: visible },
    }));
  }, []);

  const setAddedCshVisibility = useCallback((visible: boolean) => {
    setState(prev => ({ ...prev, addedCsh: visible }));
  }, []);

  const value = useMemo(() => ({
    ...state,
    setGroupVisibility,
    setCshGroupVisibility,
    setAddedCshVisibility,
  }), [state, setGroupVisibility, setCshGroupVisibility, setAddedCshVisibility]);

  return (
    <SidebarVisibilityContext.Provider value={value}>
      {children}
    </SidebarVisibilityContext.Provider>
  );
};

export const useSidebarVisibility = () => useContext(SidebarVisibilityContext);
